import { MAX_PACKAGE_OPS, type PackageOp, type PackageStoresOp } from "~/utils/allocationPackageOps"

// The package draft is the ordered list of ops Publish sends. Staging appends,
// folding an op into the one right before it when the two name the same thing.

function mergeStoreSets(earlier: PackageStoresOp, later: PackageStoresOp): PackageStoresOp {
  const add = new Set(earlier.add)
  const remove = new Set(earlier.remove)
  for (const id of later.add) {
    if (remove.has(id)) remove.delete(id)
    else add.add(id)
  }
  for (const id of later.remove) {
    if (add.has(id)) add.delete(id)
    else remove.add(id)
  }
  return { ...earlier, add: [...add], remove: [...remove] }
}

/** Ops that a later op of the same kind on the same item replaces. */
const REPLACEABLE: ReadonlySet<PackageOp["kind"]> = new Set([
  "renamePackage",
  "setPackageActive",
  "renameFolder",
  "setFolderStores",
  "setFolderActive",
])

function itemOf(op: PackageOp) {
  if ("packageId" in op) return op.packageId
  if ("folderId" in op) return op.folderId
  return null
}

/** The op that replaces `last` when it follows it, null when the two stay apart. */
function folded(last: PackageOp, op: PackageOp): PackageOp | "dropBoth" | null {
  if (last.kind === "setPackageStores" && op.kind === "setPackageStores") {
    if (last.packageId !== op.packageId) return null
    const merged = mergeStoreSets(last, op)
    return merged.add.length + merged.remove.length === 0 ? "dropBoth" : merged
  }
  if (last.kind !== op.kind || !REPLACEABLE.has(op.kind)) return null
  return itemOf(last) === itemOf(op) ? op : null
}

/** The draft with `op` staged after everything before it. */
export function stageOp(ops: PackageOp[], op: PackageOp): PackageOp[] {
  const last = ops.at(-1)
  const merged = last && folded(last, op)
  if (!merged) return [...ops, op]
  return merged === "dropBoth" ? ops.slice(0, -1) : [...ops.slice(0, -1), merged]
}

const TOO_MANY_CHANGES = "That is too many unpublished changes. Publish or discard them first."

/** The draft with a batch of staged changes folded in, or why it can't take them. */
export function stageBatch(
  ops: PackageOp[],
  batch: PackageOp[],
): { ops: PackageOp[] } | { error: string } {
  const next = batch.reduce(stageOp, ops)
  return next.length > MAX_PACKAGE_OPS ? { error: TOO_MANY_CHANGES } : { ops: next }
}

/** Assigning or unassigning stores against the package's list as staged so far; null when nothing moves. */
export function storesToggleOp(
  packageId: string,
  current: ReadonlySet<string>,
  storeIds: string[],
  assign: boolean,
): PackageStoresOp | null {
  const moving = storeIds.filter((id) => current.has(id) !== assign)
  if (moving.length === 0) return null
  return {
    kind: "setPackageStores",
    packageId,
    add: assign ? moving : [],
    remove: assign ? [] : moving,
  }
}

/** A store list with store ops still waiting for their preview laid over it. */
export function withStoreOps(storeIds: string[], ops: PackageStoresOp[]): string[] {
  if (ops.length === 0) return storeIds
  const ids = new Set(storeIds)
  for (const op of ops) {
    for (const id of op.add) ids.add(id)
    for (const id of op.remove) ids.delete(id)
  }
  return [...ids]
}
